import { DOCUMENT, NgIf } from '@angular/common';
import { Component, Inject, OnInit, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GtLocalStorageService } from '../../../core/services/gt-local-storage.service';
import { GtOAuthService } from '../../../core/services/gt-oauth.service';
import { GtSessionStorageService } from '../../../core/services/gt-session-storage.service';
import { LoginService } from '../../../core/services/login.service';
import { UtilsService } from '../../../core/services/utils';
import { isUndefined, isNull, isEmpty } from 'lodash-es';
import { LoginResponse } from '../../models/login/LoginResponse';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'openid-callback',
    templateUrl: './openid-callback.component.html',
    styleUrls: ['./openid-callback.component.scss'],
    imports: [NgIf, NgbAlert]
})
export class OpenidCallbackComponent implements OnInit {

  public errorMsg: string;
  public processing: boolean;
  private queryObj: any;
  private signInProvider: string;
  private challenge: string;
  private destroyRef = inject(DestroyRef);

  constructor(private route: ActivatedRoute,
              private router: Router,
              private loginService: LoginService,
              private utilsService: UtilsService,
              @Inject(DOCUMENT) private document: any,
              private gtOauthService: GtOAuthService,
              private sessionStorageService: GtSessionStorageService,
              private _localStorageService: GtLocalStorageService,
              private utils: UtilsService) {
    this.loginSubscriber = this.loginSubscriber.bind(this);
    this.loginErrorSubscriber = this.loginErrorSubscriber.bind(this);
  }

  public ngOnInit() {
    this.loadFragments();
    this.loadQueryParams();
  }

  private loadFragments() {
    this.route.fragment
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (isNull(params)) {
          return;
        }
        this.queryObj = this.utilsService.getQueryParams(params);
        if (this.queryObj.error) {
          this.errorMsg = this.queryObj.error_description;
        } else {
          this._localStorageService.removeAll();
          const encodedState = this.queryObj.state;
          const customParams = this.getDecodedCustomParams(encodedState);
          this.setCustomValues(customParams);
          this.processing = true;
          const token = isUndefined(this.queryObj.id_token) ? this.queryObj.access_token : this.queryObj.id_token;
          this.authenticateLogin(token);
        }
      });
  }

  private loadQueryParams() {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (isUndefined(params) || isEmpty(params)) {
          return;
        }
        const code = params['code'];
        const encodedState = params['state'];
        const error = params['error'];
        if (!isUndefined(error)) {
          this.errorMsg = error;
        }
        const customParams = this.getDecodedCustomParams(encodedState);
        this.setCustomValues(customParams);
        this.processing = true;
        this.authenticateLogin(code);
      });
  }

  private authenticateLogin(token: string) {
    this.loginService
      .openIdLogin(this.challenge, this.signInProvider, token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.loginSubscriber, this.loginErrorSubscriber);
  }

  private loginSubscriber(res: LoginResponse) {
    this.processing = false;
    if (this.loginService.isRedirect(res)) {
      this.document.location.href = res.redirectUrl;
    }
  }

  private loginErrorSubscriber(res: any) {
    this.processing = false;
    const {error} = res;
    console.error('error', error.message);
    this.errorMsg = error.message;
  }

  private getDecodedCustomParams(encodedState) {
    const state = window.atob(encodedState);
    return JSON.parse(state);
  }

  private setCustomValues(customParams): void {
    this._localStorageService.put('nonce', customParams.nonce);
    this.signInProvider = customParams.provider;
    this.challenge = customParams.challenge;
  }
}

